import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Always send cookies with API calls.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  // null = still checking, false = anonymous, object = signed-in
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (err) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback exchange the session_id
    // and set the cookie before we probe /auth/me.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async ({ email, password }) => {
    const res = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async ({ email, password, name }) => {
    const res = await axios.post(
      `${API_URL}/api/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      // ignore — we're clearing local state either way
    }
    setUser(false);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    await axios.post(
      `${API_URL}/api/auth/forgot-password`,
      { email },
      { withCredentials: true }
    );
  }, []);

  const resetPassword = useCallback(async ({ token, password }) => {
    const res = await axios.post(
      `${API_URL}/api/auth/reset-password`,
      { token, password },
      { withCredentials: true }
    );
    setUser(res.data.user);
    return res.data.user;
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signInWithGoogle = useCallback(() => {
    const redirectUrl = `${window.location.origin}/account`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const value = {
    user: user || null,
    isAuthenticated: Boolean(user && typeof user === 'object'),
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    signInWithGoogle,
    refresh: checkAuth,
    formatError: formatApiErrorDetail,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
};

export { formatApiErrorDetail };
